import { Component } from "react";

/**
 * Last-resort boundary around lazy-loaded routes. If a chunk load fails
 * even after the retry + auto-reload path in lazyWithRetry, or if a page
 * throws at runtime, this shows a friendly message with a manual reload
 * button instead of a blank screen.
 */
export default class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: String(error?.message || error) };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="route-error-boundary"
        className="min-h-[70vh] flex items-center justify-center p-8"
      >
        <div className="max-w-md text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-2">
            Something went wrong
          </div>
          <div className="text-sm text-zinc-700 mb-4">
            This page failed to load. This usually happens right after a new
            version is deployed — a full refresh will fix it.
          </div>
          <button
            onClick={() => {
              window.sessionStorage.removeItem("__chunk_reload_attempted__");
              window.location.reload();
            }}
            className="px-4 h-9 bg-zinc-950 text-white text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-zinc-800"
            data-testid="route-error-reload-btn"
          >
            Reload page
          </button>
          {this.state.message && (
            <div className="text-[10px] text-zinc-400 mt-4 font-mono break-all">
              {this.state.message}
            </div>
          )}
        </div>
      </div>
    );
  }
}
